import React, { useState } from 'react';
import { Save } from 'lucide-react';
import useAuthStore from '../store/authStore';
import { fetchWithTenant } from '../utils/apiClient';
import configs from '../config';

export default function SettingsProfile() {
  const { user, updateUser } = useAuthStore();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
  });

  const handleSave = async () => {
    setIsSaving(true);
    setMessage(null);
    try {
      const response = await fetchWithTenant(`${configs.BACKEND_URL}/api/user/profile`, {
        method: 'PUT',
        body: JSON.stringify({ name: formData.name }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data?.user?.name) {
          updateUser({ name: data.user.name });
        }
        setMessage({ type: 'success', text: 'Profile updated successfully.' });
        setIsEditing(false);
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.error || 'Failed to update profile.' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Something went wrong.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({ name: user?.name || '', email: user?.email || '' });
    setIsEditing(false);
    setMessage(null);
  };

  return (
    <div>
      <h2 className="text-lg font-medium text-gray-900 mb-4">Profile</h2>

      {message && (
        <div className={`mb-4 px-3 py-2 rounded-md text-sm ${
          message.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Avatar */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center">
          <span className="text-xl font-semibold text-primary-700">
            {user?.name?.charAt(0)?.toUpperCase() || '?'}
          </span>
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900">{user?.name}</p>
          <p className="text-sm text-gray-500">{user?.role}</p>
        </div>
      </div>

      {/* Form */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
          {isEditing ? (
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          ) : (
            <p className="text-sm text-gray-900 py-2">{user?.name || '—'}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <p className="text-sm text-gray-900 py-2">{user?.email || '—'}</p>
          <p className="text-xs text-gray-400">Email cannot be changed.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
          <p className="text-sm text-gray-900 py-2 capitalize">{user?.role || '—'}</p>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 flex items-center gap-3">
        {isEditing ? (
          <>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              <Save className="w-4 h-4" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Edit Profile
          </button>
        )}
      </div>
    </div>
  );
}
